import type { ImageParams } from 'components/Common/ResponsiveImage'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import { rem } from 'polished'
import type { ReactElement } from 'react'
import { useCallback } from 'react'
import styled from 'styled-components'
import {
  COMPACT_SEARCH_TILE_IMAGE_COLUMN_WIDTH,
  COMPACT_SEARCH_TILE_MOSAIC_GUTTER,
  COMPACT_SEARCH_TILE_MOSAIC_MIN_HEIGHT,
} from './searchTileLayoutStyles'

const MOSAIC_RADIUS = rem(8)
const BOTTOM_ASPECT_CSS = '3 / 2'
/** Cloudinary `ar_` values must use colon form — a slash breaks the image URL path. */
const MAIN_ASPECT_IMAGE = '3:4'
const BOTTOM_ASPECT_IMAGE = '3:2'
/** Mosaic grid needs a main + bottom image with ids; otherwise fall back to a single hero. */
const MOSAIC_MIN_IMAGES = 2

export function shouldShowSearchTileImageMosaic(
  images: Array<{ id?: string | null }>,
): boolean {
  return images.length >= MOSAIC_MIN_IMAGES &&
    !!images[0]?.id &&
    !!images[1]?.id
}

const mosaicImageParams: ImageParams = {
  quality: 'best',
  fit: 'center',
  objectFit: 'cover',
  mobileWidth: `${COMPACT_SEARCH_TILE_IMAGE_COLUMN_WIDTH}px`,
}

const Container = styled.div`
  grid-area: hero;
  position: relative;
  align-self: stretch;
  width: ${rem(COMPACT_SEARCH_TILE_IMAGE_COLUMN_WIDTH)};
  height: 100%;
  min-height: ${rem(COMPACT_SEARCH_TILE_MOSAIC_MIN_HEIGHT)};
  border-radius: ${MOSAIC_RADIUS};
  overflow: hidden;
`

const MosaicGrid = styled.div`
  display: grid;
  height: 100%;
  min-height: inherit;
  grid-template:
    "main" minmax(0, 1fr)
    "thumb" auto / 1fr;
  gap: ${rem(COMPACT_SEARCH_TILE_MOSAIC_GUTTER)};
`

const ImageCell = styled.div`
  position: relative;
  overflow: hidden;
  min-width: 0;
  min-height: 0;

  > picture {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
  }
`

const SingleImage = styled(ImageCell)`
  height: 100%;
  min-height: inherit;
`

const MainImage = styled(ImageCell)`
  grid-area: main;
`

const BottomImage = styled(ImageCell)`
  grid-area: thumb;
  aspect-ratio: ${BOTTOM_ASPECT_CSS};
`

const TileImage = styled(ResponsiveImage)`
  display: block;
  width: 100%;
  height: 100%;
`

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  padding: ${rem(8)};
  pointer-events: none;
  display: grid;
  gap: ${rem(8)};
  grid-template-areas:
    "top-left top-right"
    "bottom-left bottom-right";
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  align-items: start;

  > .top-left {
    grid-area: top-left;
    justify-self: start;
    pointer-events: auto;
  }

`

interface Props {
  images: Array<App.Image>
  productLabel?: ReactElement | false
  eagerLoadFirstImage?: boolean
  onImageLoad?: (index: number, image: App.Image) => void
}

function SearchTileImageMosaic({
  images,
  productLabel,
  eagerLoadFirstImage,
  onImageLoad,
}: Props) {
  const showMosaic = shouldShowSearchTileImageMosaic(images)
  const primaryImage = images[0]
  const secondaryImage = images[1]
  const primaryImageId = primaryImage?.id
  const secondaryImageId = secondaryImage?.id

  const handlePrimaryImageLoad = useCallback(() => {
    if (!primaryImage || !onImageLoad) return
    onImageLoad(0, primaryImage)
  }, [onImageLoad, primaryImage])

  const overlay = <Overlay>
    {!!productLabel && <div className="top-left">{productLabel}</div>}
  </Overlay>

  if (showMosaic && primaryImageId && secondaryImageId) {
    return <Container className={SearchTileImageMosaic.name} data-testid="search-tile-image-mosaic">
      <MosaicGrid>
        <MainImage>
          <TileImage
            {...mosaicImageParams}
            id={primaryImageId}
            alt={primaryImage.title ?? ''}
            aspectRatio={MAIN_ASPECT_IMAGE}
            loading={eagerLoadFirstImage ? 'eager' : 'lazy'}
            fetchPriority={eagerLoadFirstImage ? 'high' : undefined}
            onLoad={handlePrimaryImageLoad}
          />
        </MainImage>
        <BottomImage>
          <TileImage
            {...mosaicImageParams}
            id={secondaryImageId}
            alt={secondaryImage.title ?? ''}
            aspectRatio={BOTTOM_ASPECT_IMAGE}
            loading="lazy"
          />
        </BottomImage>
      </MosaicGrid>
      {overlay}
    </Container>
  }

  return <Container className={SearchTileImageMosaic.name} data-testid="search-tile-image-mosaic">
    <SingleImage data-testid="search-tile-image-mosaic-single">
      {primaryImageId && <TileImage
        {...mosaicImageParams}
        id={primaryImageId}
        alt={primaryImage.title ?? ''}
        aspectRatio={MAIN_ASPECT_IMAGE}
        loading={eagerLoadFirstImage ? 'eager' : 'lazy'}
        fetchPriority={eagerLoadFirstImage ? 'high' : undefined}
        onLoad={handlePrimaryImageLoad}
      />}
    </SingleImage>
    {overlay}
  </Container>
}

export default SearchTileImageMosaic
